import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle, Mail } from 'lucide-react';

const LOGO_LIGHT_BG = "https://customer-assets.emergentagent.com/job_35859c3c-f3c9-45c7-a4c9-9ec823ac8280/artifacts/a7ioyzyi_3%20copy.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | submitting | sent
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setPhase('submitting');
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setPhase('sent');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit request');
      setPhase('idle');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6" data-testid="forgot-password-page">
        <div className="text-center">
          <img src={LOGO_LIGHT_BG} alt="KR Agency Hub" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Forgot your password?</h1>
          <p className="text-sm text-slate-500 mt-2">Enter your email and we&apos;ll send you a link to reset it.</p>
        </div>

        {phase === 'sent' ? (
          <>
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-4 w-4 text-emerald-700" />
              <AlertDescription className="text-emerald-900" data-testid="forgot-sent-msg">
                If an account exists for <b>{email}</b>, a reset link has been sent. Check your inbox (and spam folder). The link expires in 24 hours.
              </AlertDescription>
            </Alert>
            <Button asChild variant="outline" className="w-full"><Link to="/login">Back to login</Link></Button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@example.com" required data-testid="forgot-email-input" />
            </div>
            <Button type="submit" className="w-full" disabled={phase === 'submitting'} data-testid="forgot-submit-btn">
              {phase === 'submitting' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : <><Mail className="h-4 w-4 mr-2" />Send reset link</>}
            </Button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-sky-700 hover:text-sky-900" data-testid="forgot-back-link">Back to login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
